import stripAnsi from 'strip-ansi';
import { fuzzyCompare, stringifyProblem } from './util';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import type { Message, PotentialProblem, PotentialProblemsMessage } from '~/types/chat';

const CACHED_MESSAGES = new Set<string>();

let debounceID: ReturnType<typeof setTimeout> | undefined;

export function trackStripeIntegrationErrors(messages: Message[], currentProblems: PotentialProblem[]) {
  const lastUserMessage = messages.at(-2);

  if (
    !lastUserMessage ||
    lastUserMessage.role !== 'user' ||
    lastUserMessage.type !== 'chat' ||
    !lastUserMessage.stripeIntegration ||
    CACHED_MESSAGES.has(lastUserMessage.id)
  ) {
    return;
  }

  clearTimeout(debounceID);

  debounceID = setTimeout(() => {
    CACHED_MESSAGES.add(lastUserMessage.id);

    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.StripeErrorDuringSetup,
        properties: {
          phase: lastUserMessage.stripeIntegration?.phase,
          problemCount: currentProblems.length,
          message: currentProblems.map(stringifyProblem).join('\n\n'),
        },
      },
    });
  }, 5000);
}

// track errors that didn't get resolved
export function trackMessageErrors(messages: Message[]) {
  const [repeats, problems] = countErrorRepeats(messages);

  if (repeats > 0) {
    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.RepeatingUserError,
        properties: {
          repeats,
          message: problems.join('\n\n'),
        },
      },
    });
  }
}

// track errors that did get resolved (probably)
export function trackResolvedErrors(messages: Message[]) {
  const maybeError = messages.at(-2);

  // if an error is marked as resolved and hasn't appeared by the time the user sends another message
  if (maybeError?.type === 'problems' && maybeError.resolved) {
    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.ProbablyFixedError,
        properties: {
          message: maybeError.problems.map((msg) => stringify(msg).slice(0, 255)),
        },
      },
    });
  }
}

export function countErrorRepeats(messages: Message[]): [number, string[]] {
  const errorMessages = messages.filter((message) => {
    return message.type === 'problems';
  }) as PotentialProblemsMessage[];

  const messageList = errorMessages.map((message) => message.problems);

  const problemStrings = messageList.map((problems) => {
    return problems.map(stringify).filter((str) => !!str);
  });

  // we compare the most recent errors to previous ones
  const latest = problemStrings.pop()!.slice(0, 10);

  if (latest.length === 0) {
    return [0, latest];
  }

  return [
    problemStrings.reduce((acc, current) => {
      // this condition also returns false if current is empty
      if (latest.some((str) => current.slice(0, 10).some((curStr) => fuzzyCompare(str, curStr)))) {
        return acc + 1;
      }

      return acc;
    }, 0),
    latest,
  ];
}

export function trackAttemptFix(userContext?: string) {
  sendAnalyticsEvent({
    action: AnalyticsAction.Track,
    payload: {
      event: AnalyticsTrackEvent.AttemptFix,
      properties: {
        userContext,
      },
    },
  });
}

export function trackProblem(argProblem: PotentialProblem) {
  const problem = { ...argProblem };

  if ('output' in problem) {
    problem.output = stripAnsi(problem.output);
  }

  sendAnalyticsEvent({
    action: AnalyticsAction.Track,
    payload: {
      event: AnalyticsTrackEvent.UserlandError,
      properties: {
        problem,
      },
    },
  });
}

function stringify(problem: PotentialProblem) {
  if (problem.type === 'preview') {
    return chomp(problem.error.stack);
  } else if (problem.type === 'shell' || problem.type === 'terminal') {
    return chomp(stripAnsi(problem.output));
  } else if (problem.type === 'supabase-function' || problem.type === 'supabase-migration') {
    return chomp(problem.message);
  }

  return '';
}

function chomp(str?: string) {
  return str ? str.trim().slice(400) : '';
}
